import type { IconName } from "./iconNames.generated";

/** An icon with the colour its callers pass to `SCASvgIcon`'s `color` prop. */
export interface ColoredIcon {
  icon: IconName;
  color: string;
}

const FILE_TYPES = {
  csv: { icon: "phosphor/file-csv", color: "var(--color-coolGrey-0)" },
  doc: { icon: "phosphor/microsoft-word-logo", color: "var(--color-brand-word)" },
  figma: { icon: "phosphor/figma-logo", color: "var(--color-brand-figma)" },
  file: { icon: "phosphor/file", color: "var(--color-coolGrey-0)" },
  image: { icon: "phosphor/image-square", color: "var(--color-grape-0)" },
  pdf: { icon: "phosphor/file-pdf", color: "var(--color-brand-pdf)" },
  ppt: { icon: "phosphor/file-ppt", color: "var(--color-brand-ppt)" },
  psd: { icon: "generic/file-psd", color: "var(--color-brand-psd)" },
  sketch: { icon: "phosphor/sketch-logo", color: "var(--color-brand-sketch)" },
  txt: { icon: "phosphor/file-txt", color: "var(--color-coolGrey-0)" },
  video: { icon: "phosphor/file-video", color: "var(--color-orange-0)" },
  xls: { icon: "phosphor/microsoft-excel-logo", color: "var(--color-brand-excel)" },
  zip: { icon: "phosphor/file-zip", color: "var(--color-teal-0)" },
} as const satisfies Record<string, ColoredIcon>;

type FileType = keyof typeof FILE_TYPES;

const TYPE_BY_MIME_TYPE: Partial<Record<string, FileType>> = {
  "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "doc",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xls",
  "application/vnd.oasis.opendocument.spreadsheet": "xls",
  "text/csv": "csv",
  "application/rtf": "txt",
  "application/vnd.oasis.opendocument.text": "txt",
  "text/html": "txt",
  "text/markdown": "txt",
  "text/plain": "txt",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "ppt",
  "application/gzip": "zip",
  "application/vnd.rar": "zip",
  "application/x-7z-compressed": "zip",
  "application/x-rar-compressed": "zip",
  "application/x-tar": "zip",
  "application/x-zip-compressed": "zip",
  "application/zip": "zip",
  "application/x-photoshop": "psd",
  "image/vnd.adobe.photoshop": "psd",
  "application/vnd.sketch": "sketch",
  "application/x-sketch": "sketch",
  "application/x-figma": "figma",
  "video/mp4": "video",
  "video/webm": "video",
  "video/x-msvideo": "video",
};

const TYPE_BY_EXTENSION: Partial<Record<string, FileType>> = {
  avif: "image",
  bmp: "image",
  gif: "image",
  heic: "image",
  jpeg: "image",
  jpg: "image",
  png: "image",
  svg: "image",
  tif: "image",
  tiff: "image",
  webp: "image",
  doc: "doc",
  docx: "doc",
  xls: "xls",
  xlsx: "xls",
  ods: "xls",
  csv: "csv",
  pdf: "pdf",
  htm: "txt",
  html: "txt",
  md: "txt",
  odt: "txt",
  rtf: "txt",
  txt: "txt",
  ppt: "ppt",
  pptx: "ppt",
  mp4: "video",
  webm: "video",
  avi: "video",
  "7z": "zip",
  gz: "zip",
  rar: "zip",
  tar: "zip",
  zip: "zip",
  psd: "psd",
  sketch: "sketch",
  fig: "figma",
  jam: "figma",
};

const typeByMimeType = (mimeType: string): FileType | undefined => {
  const type = mimeType.toLowerCase();
  return TYPE_BY_MIME_TYPE[type] ?? (type.startsWith("image/") ? "image" : undefined);
};

const typeByExtension = (extension: string): FileType | undefined =>
  TYPE_BY_EXTENSION[extension.replace(/^\./, "").toLowerCase()];

const extensionOfFileName = (fileName: string): string =>
  fileName.includes(".") ? (fileName.split(".").pop() ?? "") : "";

/**
 * Icon of a file by the first of its facts that is a known type: `mimeType`, then `extension`, then the extension
 * of `fileName`. `extension` is read as a MIME type or as a bare extension, since stored files carry either.
 * An unknown, empty or missing type gets the plain file icon.
 */
export const fileTypeIcon = ({
  mimeType,
  extension,
  fileName,
}: {
  mimeType?: string;
  extension?: string;
  fileName?: string;
}): ColoredIcon =>
  FILE_TYPES[
    typeByMimeType(mimeType ?? "") ??
      typeByMimeType(extension ?? "") ??
      typeByExtension(extension ?? "") ??
      typeByExtension(extensionOfFileName(fileName ?? "")) ??
      "file"
  ];
